import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import "../../assets/css/aunjai-loandetail.css";
import LoadingPage from "../../components/LoadingPage/LoadingPage";
import FooterButton from "../../components/ui/Button/FooterButton";
import { useFetchLoans } from "../../hooks/fetch-data/useFetchLoans";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useUser } from "../../hooks/useUser";
import { useLoanStore } from "../../stores/useLoanStore";
import { getProductImageUrl } from "../../utils/image";
import { getLoanAdvantagesText, getLoanAdvantagesHtml } from "../../utils/loan";
import { useFetchProductDetail } from "../../hooks/fetch-data/useFetchProductDetail";

const LoanDetailPage = () => {
  const { t } = useTranslation("selectLoan");
  const bankAccountTranslations = {
    BBL: t("bank1"),
    KBANK: t("bank2"),
    KTB: t("bank3"),
    SCB: t("bank4"),
    BAY: t("bank5"),
  };

  const borrowerTypeTranslations = {
    1: t("type1"),
    2: t("type2"),
    3: t("type3"),
  };
  const { productId } = useParams();

  const { userData } = useUser();

  const isForeigner = userData?.is_foreigner;

  const [selectedBorrowerType, setSelectedBorrowerType] = useState(1);
  const [selectedDetailImage, setSelectedDetailImage] = useState(null);

  const { data: loans, isFetching: isFetchingLoans } = useFetchLoans();

  const baseLoanInfo = loans?.find((loan) => loan.id === parseInt(productId));

  const { data: productDetail, isFetching: isFetchingDetail } = useFetchProductDetail(productId);

  // Merge list data with detail data (detail has remarks, advantages, detail_images)
  const loanInfo = baseLoanInfo
    ? { ...baseLoanInfo, ...productDetail }
    : null;

  const setSelectedLoan = useLoanStore((state) => state.setSelectedLoan);

  const navigate = useNavigate();

  const isDesktop = useIsDesktop();

  const registerLoan = () => {
    if (!loanInfo) return;

    setSelectedLoan(loanInfo);

    navigate(`/loan-process`);
  };

  if (isFetchingLoans || isFetchingDetail) return <LoadingPage message={t("loandingSelectLoan")} />;

  if (!loanInfo) return null;

  const acceptedBankAccount =
    loanInfo.condition[`bank_accout_type${selectedBorrowerType}`];

  const filteredBorrowerTypes = loanInfo.condition.borrower_type.filter(
    (type) => (isForeigner ? type === 1 : true)
  );

  return (
    <>
      <div id="pageLoanDetail">
        <div className="wrapContent">
          <div id="pLoanDetail">
            <div className="bLoanDetail">
              <div className="bContent">
                {/* Left: Image + Thumbnails */}
                <div className="bImg">
                  <img
                    src={
                      selectedDetailImage ??
                      loanInfo.loan_image ??
                      "/assets/img/ci/img/img-pico-full.png"
                    }
                  ></img>

                  {loanInfo.detail_images?.length > 0 && (
                    <div
                      className="flex gap-2 px-4 py-3 overflow-x-auto"
                      style={{ WebkitOverflowScrolling: "touch", flexWrap: "wrap" }}
                    >
                      <button
                        onClick={() => setSelectedDetailImage(null)}
                        className="flex-shrink-0"
                      >
                        <img
                          src={
                            loanInfo.loan_image ??
                            "/assets/img/ci/img/img-pico-full.png"
                          }
                          alt="Main"
                          className="rounded-lg object-cover"
                          style={{
                            width: 80,
                            height: 80,
                            border:
                              selectedDetailImage === null
                                ? "2px solid var(--color-primary, #FF6500)"
                                : "2px solid transparent",
                          }}
                        />
                      </button>
                      {loanInfo.detail_images
                        .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
                        .map((detail) => (
                          <button
                            key={detail.id}
                            onClick={() => setSelectedDetailImage(detail.image)}
                            className="flex-shrink-0"
                          >
                            <img
                              src={detail.image}
                              alt={detail.name || "Detail"}
                              className="rounded-lg object-cover"
                              style={{
                                width: 80,
                                height: 80,
                                border:
                                  selectedDetailImage === detail.image
                                    ? "2px solid var(--color-primary, #FF6500)"
                                    : "2px solid transparent",
                              }}
                            />
                          </button>
                        ))}
                    </div>
                  )}
                </div>

                {!isDesktop && (
                  <div className="bBack">
                    <Link to="/select-loan"></Link>
                  </div>
                )}

                {/* Right: Details */}
                <div className="bDetail">
                  <div className="bTitle">
                    <h2>{loanInfo?.name}</h2>
                    <div
                      style={{ fontSize: "14px", lineHeight: 1.7, color: "var(--color-text-secondary, #78706E)" }}
                      dangerouslySetInnerHTML={{ __html: getLoanAdvantagesHtml(loanInfo) }}
                    />
                  </div>
                  {/* เกณฑ์การสมัคร — hidden */}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section: เรียนรู้เพิ่มเติม (Remarks from product) */}
        {loanInfo?.remarks && (
          <div
            style={{
              maxWidth: "960px",
              margin: "0 auto",
              padding: isDesktop ? "48px 24px" : "32px 20px",
              paddingBottom: isDesktop ? "48px" : "100px",
              backgroundColor: "var(--color-bg-gray, #F7F7F7)",
            }}
          >
            <h2
              style={{
                fontSize: isDesktop ? "24px" : "20px",
                fontWeight: 700,
                color: "var(--color-text-primary, #322E2D)",
                marginBottom: "24px",
              }}
            >
              เรียนรู้เพิ่มเติม
            </h2>
            <div
              style={{
                fontSize: "14px",
                lineHeight: 1.8,
                color: "var(--color-text-secondary, #78706E)",
                backgroundColor: "#fff",
                borderRadius: "var(--radius-card, 15px)",
                padding: isDesktop ? "32px" : "20px",
                boxShadow: "var(--shadow-card-sm, 3px 3px 4px -2px rgba(193,187,186,1))",
              }}
              dangerouslySetInnerHTML={{ __html: loanInfo.remarks }}
            />
          </div>
        )}
      </div>
      <FooterButton onSubmit={registerLoan} submitButtonTitle={t("register")} />
    </>
  );
};

export default LoanDetailPage;
